import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getCancelReasons, cancelOrderByReason } from '../../shared/repos/graphql/order';
import Loader from '../Loader/Loader';

const clearDarkIcon = require('./imgs/clear-dark-icon.svg');

function CancelOrderModal({ orderCode, isOpen, onClose, onCancelled }) {
  const [showReasons, setShowReasons] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setShowReasons(false);
      setSelectedReason(null);
      setError(null);

      getCancelReasons()
        .then(setReasons)
        .catch((err) => {
          setError(err);
        });
    }
  }, [isOpen]);

  const handleConfirmCancel = async () => {
    if (!selectedReason) return;
    try {
      setLoading(true);
      await cancelOrderByReason(orderCode, selectedReason);
      if (onCancelled) onCancelled();
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl p-6 w-96 relative shadow-lg">

        {/* Close Button */}
        <div
          role="button"
          tabIndex={0}
          className="absolute top-4 right-4 text-gray-400 hover:text-black text-xl p-1"
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Enter' && onClose()}
          aria-label="Close modal"
        >
          <img src={clearDarkIcon} alt="" className="w-6 h-6 object-contain" />
        </div>

        {!showReasons ? (
          <>
            <h2 className="text-lg font-bold m-4 text-center">
              Are you sure you want to cancel your order?
            </h2>
            <div className="flex flex-col items-center gap-4 mt-4">
              <button
                type="button"
                onClick={onClose}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onClose()}
                className="bg-light text-dark px-6 py-2 rounded-full w-64 text-center"
              >
                Keep Order
              </button>
              <button
                type="button"
                onClick={() => setShowReasons(true)}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setShowReasons(true)}
                className="bg-button text-light px-6 py-2 rounded-full w-64 text-center"
              >
                Cancel Order
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-left">Reason for cancellation</h2>
            <p className="text-sm mb-4 text-left">Please provide a reason for cancelling this order.</p>
            <div className="mb-4 text-left">
              <div className="flex flex-col gap-4">
                {reasons.map((reason) => (
                  <div
                    key={reason.id}
                    role="button"
                    tabIndex={0}
                    className="flex items-center cursor-pointer text-sm gap-3 font-bold outline-none"
                    onClick={() => setSelectedReason(reason.id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                        setSelectedReason(reason.id);
                        }
                    }}
                    aria-pressed={selectedReason === reason.id}
                  >
                    <div
                      className={`w-5 h-5 border border-t-2 border-l-2 rounded-sm flex items-center justify-center ${
                        selectedReason === reason.id ? 'bg-dark border-dark' : 'border-dark'
                      }`}
                    >
                      {selectedReason === reason.id && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          viewBox="0 0 24 24"
                        >
                          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span>{reason.reason}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleConfirmCancel}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmCancel()}
              disabled={!selectedReason || loading}
              className={`w-full py-2 font-bold flex justify-center items-center ${
                  !selectedReason || loading ? 'bg-light text-gray-500' : 'bg-dark text-white'
              }`}
            >
              {loading ? (
                <Loader height={24} />
              ) : (
                  'Done'
            )}
            </button>

            {error && (
              <p className="text-red-500 mt-2" id="cancel-order-error">
                {error.message}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

CancelOrderModal.propTypes = {
  orderCode: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancelled: PropTypes.func,
};

CancelOrderModal.defaultProps = {
  onCancelled: null,
};

export default CancelOrderModal;
